import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Alert,
  Button,
  Input,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Spinner,
  Table,
} from "reactstrap";

import {
  confirmInventoryNfeImport,
  InventoryNfePreviewLine,
  previewInventoryNfeImport,
} from "../../services/inventoryNfeService";
import { getProducts, Product } from "../../services/productService";
import { Unit } from "../../services/unitService";
import { formatMoneyFromCents } from "../../helpers/money";

type ProductNfeImportOffcanvasProps = {
  isOpen: boolean;
  units: Unit[];
  preferredUnitId?: string;
  onClose: () => void;
  onImported: () => void;
};

type EditableLine = InventoryNfePreviewLine & {
  selected_product_id: string;
};

const ProductNfeImportOffcanvas = ({
  isOpen,
  units,
  preferredUnitId,
  onClose,
  onImported,
}: ProductNfeImportOffcanvasProps) => {
  const { t, i18n } = useTranslation();
  const [unitId, setUnitId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [lines, setLines] = useState<EditableLine[]>([]);
  const [catalog, setCatalog] = useState<Product[]>([]);
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [loadCatalogError, setLoadCatalogError] = useState(false);

  const reset = useCallback(() => {
    setFile(null);
    setLines([]);
    setPreviewing(false);
    setConfirming(false);
    setLoadCatalogError(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      reset();
      return;
    }

    const initial =
      preferredUnitId && preferredUnitId !== ""
        ? preferredUnitId
        : units[0]
          ? String(units[0].id)
          : "";
    setUnitId(initial);
  }, [isOpen, preferredUnitId, reset, units]);

  const loadCatalog = useCallback(async (selectedUnit: number) => {
    setLoadCatalogError(false);
    try {
      const response = await getProducts({
        unit_id: selectedUnit,
        per_page: 100,
      });
      setCatalog(response.data ?? []);
    } catch {
      setCatalog([]);
      setLoadCatalogError(true);
    }
  }, []);

  useEffect(() => {
    if (!isOpen || !unitId) {
      return;
    }
    void loadCatalog(Number(unitId));
  }, [isOpen, loadCatalog, unitId]);

  const unmatchedCount = useMemo(
    () => lines.filter((line) => !line.selected_product_id).length,
    [lines]
  );

  const handlePreview = async () => {
    if (!unitId) {
      toast.error(t("studio.products.nfe.toast.unitRequired"));
      return;
    }
    if (!file) {
      toast.error(t("studio.products.nfe.toast.fileRequired"));
      return;
    }

    setPreviewing(true);
    try {
      const response = await previewInventoryNfeImport(Number(unitId), file);
      const next = (response.data ?? []).map((line) => ({
        ...line,
        selected_product_id:
          line.product_id != null ? String(line.product_id) : "",
      }));
      setLines(next);
      if (next.length === 0) {
        toast.info(t("studio.products.nfe.toast.emptyPreview"));
      }
    } catch (error: any) {
      toast.error(error?.data || t("studio.products.nfe.toast.previewError"));
      setLines([]);
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirm = async () => {
    if (!unitId) {
      toast.error(t("studio.products.nfe.toast.unitRequired"));
      return;
    }

    const ready = lines.filter((line) => line.selected_product_id);
    if (ready.length === 0) {
      toast.error(t("studio.products.nfe.toast.noMappedLines"));
      return;
    }
    if (unmatchedCount > 0) {
      toast.error(
        t("studio.products.nfe.toast.unmatchedRemaining", {
          count: unmatchedCount,
        })
      );
      return;
    }

    setConfirming(true);
    try {
      const response = await confirmInventoryNfeImport(
        Number(unitId),
        ready.map((line) => ({
          product_id: Number(line.selected_product_id),
          quantity: line.quantity,
          cost_price_cents: line.cost_price_cents,
          expires_at: line.expires_at,
          c_prod: line.c_prod,
          c_ean: line.c_ean,
          x_prod: line.x_prod,
        }))
      );
      toast.success(
        response.message ||
          t("studio.products.nfe.toast.importSuccess", {
            count: response.data?.batch_ids?.length ?? ready.length,
          })
      );
      onImported();
      onClose();
    } catch (error: any) {
      toast.error(error?.data || t("studio.products.nfe.toast.importError"));
    } finally {
      setConfirming(false);
    }
  };

  const formatExpiry = (iso: string | null): string => {
    if (!iso) {
      return "—";
    }
    try {
      return new Intl.DateTimeFormat(i18n.language, {
        dateStyle: "short",
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={onClose}
      direction="end"
      style={{ width: "min(720px, 100vw)" }}
    >
      <OffcanvasHeader toggle={onClose}>
        {t("studio.products.nfe.title")}
      </OffcanvasHeader>
      <OffcanvasBody>
        <p className="text-muted small mb-3">
          {t("studio.products.nfe.help")}
        </p>

        <div className="mb-3">
          <label className="form-label" htmlFor="nfe-unit">
            {t("studio.products.nfe.fields.unit")}
          </label>
          <Input
            id="nfe-unit"
            type="select"
            value={unitId}
            onChange={(event) => {
              setUnitId(event.target.value);
              setLines([]);
            }}
          >
            <option value="">{t("studio.products.nfe.fields.unitPlaceholder")}</option>
            {units.map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.name}
              </option>
            ))}
          </Input>
        </div>

        <div className="mb-3">
          <label className="form-label" htmlFor="nfe-xml">
            {t("studio.products.nfe.fields.xml")}
          </label>
          <Input
            id="nfe-xml"
            type="file"
            accept=".xml,text/xml,application/xml"
            onChange={(event) => {
              const next = event.target.files?.[0] ?? null;
              setFile(next);
              setLines([]);
            }}
          />
        </div>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <Button
            color="primary"
            outline
            disabled={previewing || !file || !unitId}
            onClick={() => void handlePreview()}
          >
            {previewing ? (
              <Spinner size="sm" className="me-1" />
            ) : (
              <i className="ri-eye-line me-1" />
            )}
            {t("studio.products.nfe.actions.preview")}
          </Button>
          <Button
            color="primary"
            disabled={confirming || lines.length === 0}
            onClick={() => void handleConfirm()}
          >
            {confirming ? (
              <Spinner size="sm" className="me-1" />
            ) : (
              <i className="ri-check-line me-1" />
            )}
            {t("studio.products.nfe.actions.confirm")}
          </Button>
        </div>

        {loadCatalogError ? (
          <Alert color="warning" className="mb-3">
            {t("studio.products.nfe.toast.catalogLoadError")}
          </Alert>
        ) : null}

        {lines.length === 0 && !previewing ? (
          <div className="text-center text-muted py-5">
            <i className="ri-file-upload-line display-6 d-block mb-2" />
            {t("studio.products.nfe.empty")}
          </div>
        ) : null}

        {lines.length > 0 ? (
          <>
            {unmatchedCount > 0 ? (
              <Alert color="warning" className="mb-3">
                {t("studio.products.nfe.unmatchedBanner", {
                  count: unmatchedCount,
                })}
              </Alert>
            ) : (
              <Alert color="success" className="mb-3">
                {t("studio.products.nfe.allMatchedBanner")}
              </Alert>
            )}

            <div className="table-responsive">
              <Table size="sm" className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>{t("studio.products.nfe.columns.xmlName")}</th>
                    <th>{t("studio.products.nfe.columns.qty")}</th>
                    <th>{t("studio.products.nfe.columns.cost")}</th>
                    <th>{t("studio.products.nfe.columns.expires")}</th>
                    <th>{t("studio.products.nfe.columns.mapTo")}</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, index) => (
                    <tr
                      key={`${line.c_prod}-${index}`}
                      className={
                        line.selected_product_id ? undefined : "table-warning"
                      }
                    >
                      <td>
                        <div className="fw-medium">{line.x_prod}</div>
                        <small className="text-muted">
                          {line.c_prod}
                          {line.c_ean ? ` · ${line.c_ean}` : ""}
                        </small>
                        {!line.selected_product_id ? (
                          <div className="small text-warning mt-1">
                            {t("studio.products.nfe.unmatchedHint")}
                          </div>
                        ) : null}
                      </td>
                      <td>{line.quantity}</td>
                      <td>
                        {formatMoneyFromCents(
                          line.cost_price_cents,
                          i18n.language
                        )}
                      </td>
                      <td>{formatExpiry(line.expires_at)}</td>
                      <td style={{ minWidth: 180 }}>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={line.selected_product_id}
                          onChange={(event) => {
                            const value = event.target.value;
                            setLines((prev) =>
                              prev.map((row, rowIndex) =>
                                rowIndex === index
                                  ? { ...row, selected_product_id: value }
                                  : row
                              )
                            );
                          }}
                        >
                          <option value="">
                            {t("studio.products.nfe.fields.productPlaceholder")}
                          </option>
                          {catalog.map((product) => (
                            <option key={product.id} value={product.id}>
                              {product.name}
                            </option>
                          ))}
                        </Input>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        ) : null}
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default ProductNfeImportOffcanvas;
